import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { skills } from "../../data/constanit";
import { Code2, Server, Cloud } from "lucide-react";

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  padding: 80px 24px 60px;

  @media (max-width: 768px) {
    padding: 60px 16px 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1160px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 12px;
  letter-spacing: -0.5px;

  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  text-align: center;
  max-width: 620px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.7;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    font-size: 14.5px;
    margin-bottom: 24px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 36px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  padding: 5px;
  border-radius: 999px;

  @media (max-width: 640px) {
    border-radius: 14px;
    padding: 6px;
    width: 100%;
  }
`;

const FilterButton = styled.button`
  position: relative;
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ active, theme }) => (active ? "#030712" : theme.text_secondary)};
  transition: color 0.2s ease;
  z-index: 1;

  &:hover {
    color: ${({ active, theme }) => (active ? "#030712" : theme.text_primary)};
  }

  @media (max-width: 640px) {
    flex: 1;
    min-width: 100px;
    text-align: center;
  }
`;

const ActiveFilterPill = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, #38bdf8 100%);
  border-radius: 999px;
  z-index: -1;
`;

const SkillsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 28px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const CategoryCard = styled(motion.div)`
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 20px;
  padding: 26px 24px;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => `${theme.primary}66`};
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.4), 0 0 25px ${({ theme }) => `${theme.primary}1a`};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

const IconWrapper = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: ${({ theme }) => `${theme.primary}18`};
  border: 1px solid ${({ theme }) => `${theme.primary}33`};
  color: ${({ theme }) => theme.primary};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

const CategoryTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
`;

const SkillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const SkillChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_primary};
  font-size: 13.5px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: default;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    background: ${({ theme }) => theme.card_hover || theme.bgSoft};
    transform: translateY(-2px);
    color: ${({ theme }) => theme.primary};
  }
`;

const SkillIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const categoryIcons = {
  frontend: <Code2 size={20} />,
  backend: <Server size={20} />,
  tools: <Cloud size={20} />
};

const Skills = () => {
  const [activeFilter, setActiveFilter] = useState("all");

  const filterTabs = [
    { id: "all", label: "All Skills" },
    { id: "frontend", label: "Frontend" },
    { id: "backend", label: "Backend & DB" },
    { id: "tools", label: "Cloud & DevOps" }
  ];

  const filteredSkills =
    activeFilter === "all"
      ? skills
      : skills.filter((item) => item.category === activeFilter);

  return (
    <Container id="skills">
      <ContentWrapper>
        <Title
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Technical <span>Skills</span>
        </Title>

        <Description
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Tools and technologies I use to build clean interfaces, robust REST APIs, and scalable production systems.
        </Description>

        <FilterContainer>
          {filterTabs.map((tab) => {
            const isActive = activeFilter === tab.id;
            return (
              <FilterButton
                key={tab.id}
                active={isActive}
                onClick={() => setActiveFilter(tab.id)}
              >
                {isActive && (
                  <ActiveFilterPill
                    layoutId="activeSkillFilterPill"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                {tab.label}
              </FilterButton>
            );
          })}
        </FilterContainer>

        <SkillsGrid>
          <AnimatePresence mode="popLayout">
            {filteredSkills.map((category) => (
              <CategoryCard
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35 }}
              >
                <CardHeader>
                  <IconWrapper>
                    {categoryIcons[category.category] || <Code2 size={20} />}
                  </IconWrapper>
                  <CategoryTitle>{category.title}</CategoryTitle>
                </CardHeader>

                <SkillList>
                  {category.skills.map((skill) => (
                    <SkillChip key={skill.name}>
                      <SkillIcon
                        src={skill.image}
                        alt={skill.name}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <span>{skill.name}</span>
                    </SkillChip>
                  ))}
                </SkillList>
              </CategoryCard>
            ))}
          </AnimatePresence>
        </SkillsGrid>
      </ContentWrapper>
    </Container>
  );
};

export default Skills;
